import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
    FaSearch, FaArrowLeft, FaFlask, FaMicroscope, FaBook, FaCalendarAlt, 
    FaCheckSquare, FaLayerGroup, FaSpinner, FaTimes, FaBrain, FaChartLine,
    FaCheckCircle, FaNetworkWired, FaDatabase, FaGlobe
} from 'react-icons/fa';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { useScientific } from '../context/ScientificContext';
import Nav from '../components/Nav';
import './ScientificSearch.css';

const ScientificSearch = () => {
    const navigate = useNavigate();
    const { token } = useAuth();
    const { t, i18n } = useTranslation();
    const { 
        searchQuery: query, setSearchQuery: setQuery,
        searchResults: results, setSearchResults: setResults,
        selectedIds, setSelectedIds,
        setLastMeshTerms
    } = useScientific();

    const [loading, setLoading] = useState(false);
    const [synthesis, setSynthesis] = useState(null);
    const [generatingSynthesis, setGeneratingSynthesis] = useState(false);
    const [error, setError] = useState(null);
    const [trace, setTrace] = useState(null);
    const [activeStage, setActiveStage] = useState(-1);

    // Simulate pipeline stages while loading
    useEffect(() => {
        let interval;
        if (loading) {
            setActiveStage(0);
            interval = setInterval(() => {
                setActiveStage(prev => (prev < 3 ? prev + 1 : prev));
            }, 1200);
        } else {
            setActiveStage(-1);
            if (interval) clearInterval(interval);
        }
        return () => clearInterval(interval);
    }, [loading]);

    const handleSearch = async (e) => {
        if (e) e.preventDefault();
        if (!query.trim()) return;

        setLoading(true);
        setError(null);
        setResults([]);
        setTrace(null);
        setSelectedIds(new Set());
        try {
            const response = await axios.post('http://localhost:5000/api/scientific/search', {
                query,
                userLanguage: i18n.language
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setResults(response.data.articles || []);
            setTrace(response.data.pipelineTrace);
            if (response.data.meshTerms) setLastMeshTerms(response.data.meshTerms);
        } catch (err) {
            console.error('Search error:', err);
            setError('Error al realizar la búsqueda científica.');
        } finally {
            setLoading(false);
        }
    };

    const toggleSelection = (id) => {
        const next = new Set(selectedIds);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        setSelectedIds(next);
    };

    const handleGenerateSynthesis = async () => {
        setGeneratingSynthesis(true);
        try {
            const response = await axios.post('http://localhost:5000/api/scientific/analyze', {
                articleIds: Array.from(selectedIds),
                targetLanguage: i18n.language
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setSynthesis(response.data.synthesis);
        } catch (err) {
            console.error('Synthesis error:', err);
            setError('No se pudo generar la síntesis. Asegúrate de que los artículos hayan sido analizados previamente.');
        } finally {
            setGeneratingSynthesis(false);
        }
    };

    return (
        <div className="ss-page">
            <Nav />
            <div className="ss-container">
                <header className="ss-header">
                    <button className="ss-back-btn" onClick={() => navigate(-1)}>
                        <FaArrowLeft /> Regresar
                    </button>
                    <div className="ss-title-section">
                        <h1><FaFlask className="ss-icon-main" /> Explorador Científico</h1>
                        <p>Descubre y sintetiza evidencia científica con Inteligencia Artificial.</p>
                    </div>
                </header>

                <div className="ss-search-card">
                    <form className="ss-search-form" onSubmit={handleSearch}>
                        <div className="ss-input-wrapper">
                            <FaMicroscope className="ss-search-inner-icon" />
                            <input 
                                type="text" 
                                placeholder="Ingresa un tema, fármaco o patología..."
                                value={query}
                                onChange={(e) => setQuery(e.target.value)}
                            />
                        </div>
                        <button type="submit" className="ss-btn-search" disabled={loading}>
                            {loading ? <FaSpinner className="ss-spin" /> : <><FaSearch /> Buscar</>}
                        </button>
                    </form>
                </div>

                {/* Pipeline Trace Panel */}
                {(loading || trace) && (
                    <div className="ss-pipeline-card">
                        <h3><FaChartLine /> Trazabilidad del Pipeline</h3>
                        <div className="ss-pipeline-steps">
                            <div className={`ss-step ${activeStage >= 0 || trace ? 'active' : ''}`}>
                                <div className="ss-step-icon">{trace ? <FaCheckCircle className="done" /> : <FaMicroscope className="spin" />}</div>
                                <div className="ss-step-info">
                                    <span className="ss-step-label">MeSH Expansion</span>
                                    {trace && <span className="ss-step-detail">{trace.meshExtraction.meshTermsFound} términos • {trace.meshExtraction.durationMs}ms</span>}
                                </div>
                            </div>
                            <div className={`ss-step ${activeStage >= 1 || trace ? 'active' : ''}`}>
                                <div className="ss-step-icon">{trace ? <FaCheckCircle className="done" /> : <FaNetworkWired className={activeStage >= 1 ? 'spin' : ''} />}</div>
                                <div className="ss-step-info">
                                    <span className="ss-step-label">Búsqueda Multi-Fuente</span>
                                    {trace && <span className="ss-step-detail">{trace.multiSourceSearch.afterDedup} artículos únicos • {trace.multiSourceSearch.durationMs}ms</span>}
                                </div>
                            </div>
                            <div className={`ss-step ${activeStage >= 2 || trace ? 'active' : ''}`}>
                                <div className="ss-step-icon">{trace ? <FaCheckCircle className="done" /> : <FaChartLine className={activeStage >= 2 ? 'spin' : ''} />}</div>
                                <div className="ss-step-info">
                                    <span className="ss-step-label">Ranking Semántico</span>
                                    {trace && <span className="ss-step-detail">Top Score: {trace.ranking.topScore} • {trace.ranking.durationMs}ms</span>}
                                </div>
                            </div>
                            <div className={`ss-step ${activeStage >= 3 || trace ? 'active' : ''}`}>
                                <div className="ss-step-icon">{trace ? <FaCheckCircle className="done" /> : <FaDatabase className={activeStage >= 3 ? 'spin' : ''} />}</div>
                                <div className="ss-step-info">
                                    <span className="ss-step-label">Persistencia en BD</span>
                                    {trace && <span className="ss-step-detail">{trace.persistence.newArticlesSaved} nuevos guardados</span>}
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                <main className="ss-results-section">
                    {loading && <div className="ss-skeleton-grid"><div className="ss-skeleton"></div><div className="ss-skeleton"></div></div>}
                    
                    {!loading && results && results.length > 0 && (
                        <div className="ss-results-grid">
                            {results.map((article) => (
                                <div 
                                    key={article.id} 
                                    className={`ss-article-card ${selectedIds.has(article.id) ? 'selected' : ''}`}
                                    onClick={() => toggleSelection(article.id)}
                                >
                                    <div className="ss-article-header">
                                        <div className="ss-article-badge">{article.source}</div>
                                        <div className="ss-article-actions">
                                            {article.needsTranslation && <FaGlobe className="ss-translate-icon" title="Necesita traducción" />}
                                            <div className="ss-checkbox">
                                                {selectedIds.has(article.id) && <FaCheckSquare />}
                                            </div>
                                        </div>
                                    </div>
                                    <h3 className="ss-article-title">{article.title}</h3>
                                    
                                    {article.ranking && (
                                        <div className="ss-ranking-mini">
                                            <div className="ss-ranking-bar">
                                                <div className="ss-ranking-fill" style={{ width: `${Math.round(article.ranking.score * 100)}%` }}></div>
                                            </div>
                                            <span className="ss-ranking-text">Score: {Math.round(article.ranking.score * 100)}%</span>
                                        </div>
                                    )}

                                    <p className="ss-article-snippet">
                                        {article.abstract ? (article.abstract.length > 180 ? article.abstract.substring(0, 180) + '...' : article.abstract) : 'Sin resumen disponible.'}
                                    </p>
                                    <div className="ss-article-meta">
                                        <span><FaBook /> {article.journal}</span>
                                        <span><FaCalendarAlt /> {article.publicationDate}</span>
                                    </div>
                                    <div className="ss-article-footer">
                                        <button className="ss-btn-view" onClick={(e) => { e.stopPropagation(); navigate(`/scientific-article/${article.id}`); }}>
                                            Ver Análisis IA
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </main>

                {selectedIds.size > 0 && (
                    <div className="ss-floating-actions">
                        <div className="ss-floating-info">
                            <FaLayerGroup /> {selectedIds.size} artículos seleccionados
                        </div>
                        <button 
                            className="ss-btn-synthesis" 
                            onClick={handleGenerateSynthesis}
                            disabled={generatingSynthesis}
                        >
                            {generatingSynthesis ? <FaSpinner className="ss-spin" /> : 'Generar Síntesis Consolidada'}
                        </button>
                    </div>
                )}

                {synthesis && (
                    <div className="ss-synthesis-modal">
                        <div className="ss-synthesis-content">
                            <div className="ss-synthesis-header">
                                <h3><FaBrain /> Síntesis Narrativa IA</h3>
                                <button onClick={() => setSynthesis(null)}><FaTimes /></button>
                            </div>
                            <div className="ss-synthesis-body">
                                {synthesis.split('\n').map((line, i) => (
                                    <p key={i}>{line}</p>
                                ))}
                            </div>
                            <div className="ss-synthesis-footer">
                                <button className="ss-btn-export">Exportar a PDF</button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ScientificSearch;
